// Геопозиция курьера: точная фиксация при отметке точки и расстояние до неё.

import type { GeoFix } from '@/services/courier';

export class GeoError extends Error {
  constructor(
    message: string,
    readonly kind: 'denied' | 'unavailable' | 'timeout' | 'unsupported',
  ) {
    super(message);
  }
}

/** Где включить доступ. Идёт после фразы «нет доступа / разрешите геолокацию». */
export const GEO_DENIED_HINT =
  'iPhone: Настройки → Конфиденциальность и безопасность → Службы геолокации (включены) → «Сайты Safari» → «При использовании». Android: Chrome → ⋮ → Настройки → Настройки сайтов → Местоположение → разрешите этот сайт, и включите геолокацию в шторке телефона.';

function toGeoError(err: GeolocationPositionError): GeoError {
  if (err.code === err.PERMISSION_DENIED) return new GeoError('Нет доступа к геопозиции. ' + GEO_DENIED_HINT, 'denied');
  if (err.code === err.TIMEOUT) return new GeoError('GPS долго ищет спутники — выйдите на улицу и попробуйте ещё раз', 'timeout');
  return new GeoError('Не удалось определить местоположение — включите геолокацию в телефоне', 'unavailable');
}

/**
 * Одна точная фиксация. Свежая (до 10 с) из кэша браузера подходит — курьер
 * только что стоял тут же; иначе ждём спутники до 20 с.
 */
export function getPosition(timeoutMs = 20_000): Promise<GeoFix> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new GeoError('Телефон не умеет определять местоположение в браузере', 'unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }),
      (err) => reject(toGeoError(err)),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 10_000 },
    );
  });
}

/** Разрешение уже дано / запрещено / ещё не спрашивали (если браузер умеет сказать). */
export async function geoPermission(): Promise<PermissionState | 'unknown'> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    return status?.state ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Следить за разрешением на геопозицию: курьер разрешил её в настройках —
 * слежение надо запустить заново (после отказа браузер его не возобновляет).
 * Браузер не умеет — ничего не делаем; отписка всегда безопасна.
 */
export function onGeoPermissionChange(fn: (state: PermissionState) => void): () => void {
  let status: PermissionStatus | null = null;
  let stopped = false;
  const handler = () => {
    if (status) fn(status.state);
  };
  try {
    const query = navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    void query
      ?.then((s) => {
        if (stopped) return;
        status = s;
        s.addEventListener('change', handler);
      })
      .catch(() => undefined);
  } catch {
    /* старый браузер без Permissions API */
  }
  return () => {
    stopped = true;
    status?.removeEventListener('change', handler);
  };
}

export function distanceMeters(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6_371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export const formatDistance = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1).replace('.', ',')} км` : `${Math.round(m)} м`);

/** Та же проверка, что на сервере: радиус плюс погрешность (не больше 100 м). */
export function withinRadius(point: { lat: number | null; lon: number | null }, fix: GeoFix, radiusM: number): { ok: boolean; distance: number | null } {
  if (point.lat == null || point.lon == null) return { ok: true, distance: null };
  const d = distanceMeters({ lat: point.lat, lon: point.lon }, fix);
  return { ok: d <= radiusM + Math.min(fix.accuracy ?? 0, 100), distance: d };
}
